import { Controller } from '@hotwired/stimulus'

import { LocalClient } from '../clients'
import { dispatcher } from './pub_controller'
import { Location, Site } from '../types/api'
import { Geocode } from '../models'

export class UsageEventMapController extends Controller {
  static targets = ['locationName', 'address', 'site', 'placement']
  static values = {
    location: { type: String, default: '{}' },
    site: { type: String, default: '{}' },
    placement: { type: String, default: '' },
  }

  declare readonly locationNameTarget: HTMLElement
  declare readonly addressTarget: HTMLElement
  declare readonly siteTarget: HTMLElement
  declare readonly placementTarget: HTMLElement

  declare locationValue: string

  locationId = ''
  siteId = ''
  equipmentText = ''
  locationObject?: Site = undefined
  siteObject?: Site = undefined
  declare geocode: Geocode

  async handleLocationSelect(event) {
    const locationId = event.detail.value
    if (locationId !== this.locationId) {
      this.locationId = locationId
      if (locationId) {
        // clear followup state before `await` to avoid races
        this.setSiteObject(undefined)
        this.setEquipment('')
        const location: Location = await LocalClient.fetchLocation(locationId)
        this.setLocationLatlong(location)
        this.setLocationObject(location)
        dispatcher(
          this,
          'geocode',
          this.pubChannel,
          new Geocode({
            verifiedLatlong: location.latlong,
            overrideLatlong: undefined,
          }),
        )
        this.element.classList.remove('hidden')
      } else {
        this.element.classList.add('hidden')
      }
    }
  }

  async handleSiteSelect(event) {
    const siteId = event.detail.value
    if (siteId !== this.siteId) {
      this.siteId = siteId
      if (siteId) {
        // clear followup state before `await` to avoid races
        this.setEquipment('')
        const site: Site = await LocalClient.fetchSite(siteId)
        this.setSiteObject(site)
        if (site.override_lat && site.override_long) {
          dispatcher(
            this,
            'geocode',
            this.pubChannel,
            new Geocode({
              verifiedLatlong: `${site.override_lat},${site.override_long}`,
              overrideLatlong: undefined,
            }),
          )
        }
      }
    }
  }

  handleEquipmentSelect(event) {
    this.setEquipment(event.detail.value)
  }

  locationValueChanged() {
    this.setLocationObject(JSON.parse(this.locationValue))
  }

  private setLocationObject(location: Location) {
    const nameEl = this.locationNameTarget
    nameEl.innerHTML = location.name
    const addressEl = this.addressTarget.querySelector('dd')
    addressEl.innerHTML = this.getAddress(location)
  }

  private getAddress(location: Location) {
    const addr = location?.physical_address
    if (!addr) return ''
    return [
      addr.line_one,
      addr.line_two,
      [
        addr.city ? `${addr.city},` : undefined,
        addr.state_or_province,
        addr.country_code,
        addr.postal_code,
      ]
        .filter(Boolean)
        .join(' '),
    ]
      .filter(Boolean)
      .join('\n')
  }

  private setSiteObject(site?: Site) {
    const content = this.siteTarget.querySelector('dd')
    content.innerHTML = site?.name || ''
  }

  private setEquipment(equipment: string) {
    const content = this.placementTarget.querySelector('dd')
    const placement = equipment.split('/')[1] || ''
    content.innerHTML = placement
  }

  private setLocationLatlong(location: Location) {
    const verifiedLatlong = location.physical_address.verified_latlong
    const overrideLatlong = location.physical_address.override_latlong
    this.geocode = new Geocode({ verifiedLatlong, overrideLatlong })

    dispatcher(this, 'geocode', this.pubChannel, this.geocode)
  }

  get pubChannel() {
    return this.element.getAttribute('data-pub-channel-value')
  }
}
